import React from 'react';
import { render, fireEvent } from '@testing-library/react';

// Test utilities for TopBar components
export const createMockProps = (overrides = {}) => ({
  // Display Options
  showLogo: true,
  showLocation: true,
  showUserIcon: true,
  showCartIcon: true,

  // Data Props
  brandName: 'TestBrand',
  currentLocation: 'Test City',
  cartCount: 0,
  locations: ['Location 1', 'Location 2', 'Location 3'],
  signupOptions: [
    {
      id: 'customer',
      label: 'Sign up as Customer',
      action: 'signup-user',
      description: 'Book services',
      icon: '🙏'
    }
  ],

  // User State
  isLoggedIn: false,
  userInfo: null,
  userMenuItems: [
    {
      id: 'login',
      label: 'Login',
      action: 'login',
      icon: '👤',
      showWhen: 'guest'
    }
  ],

  // Event Handlers - Mock functions
  onLocationClick: jest.fn(),
  onUserClick: jest.fn(),
  onCartClick: jest.fn(),
  onSignupUser: jest.fn(),
  onSignupPandit: jest.fn(),
  onLogoClick: jest.fn(),

  // State Handlers - Mock functions
  setShowUserDropdown: jest.fn(),
  setShowLocationDropdown: jest.fn(),

  // State Props
  showUserDropdown: false,
  showLocationDropdown: false,

  // Functions
  handleLocationSelect: jest.fn(),
  handleUserMenuClick: jest.fn(),
  handleSearch: jest.fn(),
  formatCartCount: (count) => count > 99 ? '99+' : count,

  // Override with custom props
  ...overrides
});

export const createLoggedInProps = (overrides = {}) => 
  createMockProps({
    isLoggedIn: true,
    userInfo: {
      name: 'Test User',
      email: 'test@example.com'
    },
    userMenuItems: [
      {
        id: 'profile',
        label: 'My Profile',
        action: 'profile',
        icon: '👤',
        showWhen: 'authenticated'
      }
    ],
    ...overrides
  });

export const mockRefs = () => ({
  userDropdownRef: { current: document.createElement('div') },
  locationDropdownRef: { current: document.createElement('div') }
});

// Common test scenarios
export const testScenarios = {
  guest: () => createMockProps(),
  loggedIn: () => createLoggedInProps(),
  withCart: (count = 5) => createMockProps({ cartCount: count }),
  withManyCartItems: () => createMockProps({ cartCount: 150 }),
  mobileView: () => createMockProps({ showMobileSearch: true }),
  withDropdowns: () => createMockProps({ 
    showUserDropdown: true, 
    showLocationDropdown: true 
  })
};

// Assertion helpers
export const assertAccessibility = (component) => {
  // Check for proper ARIA attributes
  const buttons = component.container.querySelectorAll('button');
  buttons.forEach(button => {
    expect(button).toHaveAttribute('aria-label');
  });

  // Check for proper role attributes on dropdowns
  const menus = component.container.querySelectorAll('[role="menu"]');
  menus.forEach(menu => {
    expect(menu).toBeInTheDocument();
  });
};

export const assertKeyboardNavigation = (component, targetElement) => {
  // Test Enter key
  fireEvent.keyDown(targetElement, { key: 'Enter' });
  // Test Space key
  fireEvent.keyDown(targetElement, { key: ' ' });
  // Test Escape key
  fireEvent.keyDown(targetElement, { key: 'Escape' });
};

// Performance testing helpers
export const measureRenderTime = (Component, props) => {
  const start = performance.now();
  render(<Component {...props} />);
  const end = performance.now();
  return end - start;
};

export const checkMemoization = (Component, props) => {
  const { rerender } = render(<Component {...props} />);
  
  // Rerender with same props - should not cause re-render if memoized
  rerender(<Component {...props} />);
  
  // Rerender with different props - should cause re-render
  rerender(<Component {...props} cartCount={props.cartCount + 1} />);
};
